import React, { useEffect, useId, useState } from 'react';
import clsx from 'clsx';
import IconCancel from '@/assets/icons/icon-cancel.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { StylesInspectorHeading } from '../StylesInspectorHeading/StylesInspectorHeading';
import type { HeadingLevel } from '@builder4/shared/types/styles';

type HeadingType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface StylesHeadingsProps {
  className: string;
  onClick?: () => void;
  text?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label?: string;
  fontSizeLabel?: string;
  headingType: HeadingType; // Optional when used for body items
  enableEditing?: boolean;
  currentData: HeadingLevel;
  onUpdate: (data: HeadingLevel) => void;
}

export const StylesHeadings: React.FC<StylesHeadingsProps> = ({
  className = '',
  onClick,
  label = 'Heading H1',
  fontSizeLabel = 'px',
  headingType,
  enableEditing = true,
  currentData,
  onUpdate,
}) => {
  const [open, setOpen] = useState(false);
  const instanceId = useId();

  const handleOpenChange = (next: boolean) => {
    const w = window as unknown as { __lpColorPickerOpen?: number };
    const pickerOpen = (w.__lpColorPickerOpen ?? 0) > 0;
    // If a color picker is open and Radix tries to close because of an outside click on it, ignore.
    if (!next && pickerOpen) return;
    if (next) {
      try {
        window.dispatchEvent(
          new CustomEvent('lp:styles-edit-open', { detail: { id: instanceId } }),
        );
      } catch {
        /* noop */
      }
    }
    setOpen(next);
  };

  const handleClick = () => {
    if (onClick) onClick();
  };

  // Use the desktop font size for a static representation of the heading size when comparing to other heading types
  const sizeLabel = `${currentData.desktop.fontSize}${fontSizeLabel}`;

  // Listen for other headings opening and close this one to keep only one dropdown open at a time
  useEffect(() => {
    const onOtherOpen = (e: Event) => {
      const ev = e as CustomEvent<{ id?: string }>;
      if (ev.detail?.id !== instanceId) setOpen(false);
    };
    window.addEventListener('lp:styles-edit-open', onOtherOpen as EventListener);
    return () => window.removeEventListener('lp:styles-edit-open', onOtherOpen as EventListener);
  }, [instanceId]);

  return (
    <div
      onClick={handleClick}
      className={clsx('inline-flex h-8 w-full items-center justify-between', className)}
      data-heading={headingType}
    >
      {/* Left: name and size */}
      <div className="flex min-w-0 flex-1 items-center gap-2 pr-2">
        <div className="truncate text-xs leading-none font-medium text-neutral-500">{label}</div>
      </div>

      {/* Right: three-dots and Edit */}
      <div className="flex items-center gap-1">
        <div className="flex items-center" style={{ height: 32, width: 32 }}>
          <div className="leading-none font-medium text-neutral-300" style={{ fontSize: 10 }}>
            {sizeLabel}
          </div>
        </div>
        {enableEditing && (
          <>
            <DropdownMenu open={open} onOpenChange={handleOpenChange}>
              <DropdownMenuTrigger asChild>
                <SecondaryButton size="sm" state="default" text="Edit" icon={null} />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="bg-neutral-0 relative z-10 inline-flex flex-col items-center justify-start gap-0 rounded-lg p-0 shadow-lg outline outline-neutral-100"
                style={{ width: 248 }}
                side="right"
                align="center"
                sideOffset={16}
                collisionPadding={{ top: 4, bottom: 4 }}
                data-heading={headingType}
                onWheelCapture={(e) => e.stopPropagation()}
              >
                <div className="bg-neutral-0 sticky top-0 z-10 flex h-10 min-h-10 w-full shrink-0 items-center justify-between border-b border-neutral-100 px-3 py-2">
                  <p className="text-xs leading-tight font-semibold text-neutral-900">{label}</p>
                  <IconSimpleButton
                    icon={IconCancel}
                    size={24}
                    state="default"
                    onClick={() => setOpen(false)}
                  />
                </div>
                <div
                  className="no-scrollbar max-h-[calc(100vh-120px)] w-full overflow-y-auto overscroll-contain"
                  onWheelCapture={(e) => e.stopPropagation()}
                >
                  <StylesInspectorHeading
                    className="min-w-full"
                    variant={headingType}
                    currentData={currentData}
                    onUpdate={onUpdate}
                  />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>
    </div>
  );
};
